// src/components/LeaveRequest.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";
import "./LeaveRequest.css";

// Financial year runs April -> March
const MONTH_ORDER = [3, 4, 5, 6, 7, 8, 9, 10, 11, 0, 1, 2]; // Apr..Mar (JS month index)

const fyStartYear = (iso) => {
  const d = new Date(iso);
  return d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
};
const fyLabel = (startYear) => `${startYear}-${String(startYear + 1).slice(2)}`;

const monthName = (m) =>
  new Date(2000, m, 1).toLocaleDateString("en-GB", { month: "long" });

const isValidDate = (iso) => !!iso && !Number.isNaN(new Date(iso).getTime());

const statusClass = (status) =>
  status === "Approved"
    ? "lr-status lr-status--approved"
    : status === "Rejected"
      ? "lr-status lr-status--rejected"
      : "lr-status lr-status--pending";

const LeaveRequest = () => {
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [remarks, setRemarks] = useState({});

  // "" = auto (current FY if it has entries, else newest FY), "all" = all years
  const [selectedFY, setSelectedFY] = useState("");
  // "" = auto (current month), "all" = all months, otherwise month index as string
  const [selectedMonth, setSelectedMonth] = useState("");

  const navigate = useNavigate();

  // ---------------- helpers ----------------
  const formatRequestedOn = (leave) => {
    const rawDate =
      leave?.createdAt ||
      leave?.requestDate ||
      leave?.appliedAt ||
      leave?.submittedAt;

    if (!rawDate) return "-";

    const date = new Date(rawDate);
    if (Number.isNaN(date.getTime())) return "-";

    return date.toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatTime12 = (hhmm) => {
    if (!hhmm || !/^\d{2}:\d{2}$/.test(hhmm)) return "-";
    let [h, m] = hhmm.split(":").map(Number);
    const ampm = h >= 12 ? "PM" : "AM";
    const hh = ((h + 11) % 12) + 1;
    const mm = String(m).padStart(2, "0");
    return `${String(hh).padStart(2, "0")}:${mm} ${ampm}`;
  };

  const toMinutes = (hhmm) => {
    if (!hhmm || !/^\d{2}:\d{2}$/.test(hhmm)) return null;
    const [h, m] = hhmm.split(":").map(Number);
    return h * 60 + m;
  };

  const calcHours = (leave) => {
    // Full day default 9h
    if ((leave.duration || "Full Day") !== "Half Day") return "9 hrs";

    // Prefer explicit in/out when available
    const outMin = toMinutes(leave.halfDayOutTime || leave.halfDayTime || "");
    const inMin = toMinutes(leave.halfDayInTime || "");

    if (outMin != null && inMin != null) {
      const diff = Math.max(inMin - outMin, 0);
      return (diff / 60).toFixed(1) + " hrs";
    }
    if (outMin != null) {
      const endMin = 19 * 60; // 7 PM
      const diff = Math.max(endMin - outMin, 0);
      return (diff / 60).toFixed(1) + " hrs";
    }
    if (inMin != null) {
      const startMin = 10 * 60; // 10 AM
      const diff = Math.max(inMin - startMin, 0);
      return (diff / 60).toFixed(1) + " hrs";
    }
    return "4.5 hrs";
  };

  // --------------- data fetch ---------------
  const fetchLeaveRequests = async () => {
    try {
      const res = await api.get("/leaves", {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setLeaveRequests(Array.isArray(res.data) ? res.data : []);
    } catch {
      alert("❌ Failed to fetch leave requests");
    }
  };

  useEffect(() => {
    fetchLeaveRequests();
  }, []);

  // -------------- year / month filters -------------
  const validLeaves = useMemo(
    () => leaveRequests.filter((l) => isValidDate(l.fromDate)),
    [leaveRequests],
  );

  // financial years that have entries, newest first
  const availableFYs = useMemo(
    () =>
      Array.from(new Set(validLeaves.map((l) => fyStartYear(l.fromDate)))).sort(
        (a, b) => b - a,
      ),
    [validLeaves],
  );

  // default: current FY if it has entries, else newest FY
  const currentFY = fyStartYear(new Date().toISOString());
  const defaultFY = availableFYs.includes(currentFY)
    ? String(currentFY)
    : availableFYs[0] !== undefined
      ? String(availableFYs[0])
      : "all";

  const activeFY =
    selectedFY === "all" || availableFYs.includes(Number(selectedFY))
      ? selectedFY || defaultFY
      : defaultFY;

  const leavesInFY = useMemo(
    () =>
      activeFY === "all"
        ? validLeaves
        : validLeaves.filter(
            (l) => fyStartYear(l.fromDate) === Number(activeFY),
          ),
    [validLeaves, activeFY],
  );

  const currentMonth = new Date().getMonth();

  // months with entries, plus the current month when viewing the current FY
  // (or all years), so the current month is always available. April -> March order.
  const monthOptions = useMemo(() => {
    const showCurrent = activeFY === "all" || Number(activeFY) === currentFY;
    return MONTH_ORDER.filter(
      (m) =>
        leavesInFY.some((l) => new Date(l.fromDate).getMonth() === m) ||
        (showCurrent && m === currentMonth),
    );
  }, [leavesInFY, activeFY, currentFY, currentMonth]);

  // default: current month if available, else all months
  const defaultMonth = monthOptions.includes(currentMonth)
    ? String(currentMonth)
    : "all";

  const activeMonth =
    selectedMonth === "all"
      ? "all"
      : selectedMonth !== "" && monthOptions.includes(Number(selectedMonth))
        ? selectedMonth
        : defaultMonth;

  const filteredLeaves = useMemo(
    () =>
      activeMonth === "all"
        ? leavesInFY
        : leavesInFY.filter(
            (l) => new Date(l.fromDate).getMonth() === Number(activeMonth),
          ),
    [leavesInFY, activeMonth],
  );

  // --------------- actions ------------------
  const handleLeaveStatus = async (leaveId, status) => {
    try {
      const remark = remarks[leaveId] || "";
      await api.put(
        `/leaves/status/${leaveId}`,
        { status, remark },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        },
      );

      // 🔊 Broadcast to other tabs / dashboard
      try {
        const bc = new BroadcastChannel("leaves");
        bc.postMessage({ type: "status-changed", leaveId, status });
        bc.close();
      } catch {}
      try {
        localStorage.setItem("leaves:ping", String(Date.now()));
      } catch {}
      try {
        window.dispatchEvent(new Event("leaves:statusChanged"));
      } catch {}

      alert(`Leave marked as ${status}`);
      fetchLeaveRequests(); // refresh table (filters stay as they are)
    } catch {
      alert("❌ Failed to update status");
    }
  };

  const emptyText =
    activeMonth === "all"
      ? "No leave requests found."
      : `No leave requests found for ${monthName(Number(activeMonth))}.`;

  // ---------------- render ------------------
  return (
    <div className="lr">
      {/* Header row with Back button, centered title, right filters */}
      <div className="lr-header">
        <button
          type="button"
          onClick={() => {
            if (window.history.length > 1) navigate(-1);
            else navigate("/admin");
          }}
          className="lr-back"
          aria-label="Go back"
          title="Go back"
        >
          ← Back
        </button>

        <h2 className="lr-title">📝 Leave Requests</h2>

        <div className="lr-filters">
          <div className="lr-filter">
            <label htmlFor="lr-year">Year:</label>
            <select
              id="lr-year"
              value={activeFY}
              onChange={(e) => {
                setSelectedFY(e.target.value);
                setSelectedMonth(""); // back to auto (current month)
              }}
            >
              <option value="all">All Years</option>
              {availableFYs.map((fy) => (
                <option key={fy} value={String(fy)}>
                  FY {fyLabel(fy)}
                </option>
              ))}
            </select>
          </div>

          <div className="lr-filter">
            <label htmlFor="lr-month">Month:</label>
            <select
              id="lr-month"
              value={activeMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
            >
              <option value="all">All Months</option>
              {monthOptions.map((m) => (
                <option key={m} value={String(m)}>
                  {monthName(m)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="lr-card">
        <table className="lr-table">
          <thead>
            <tr>
              <th>Employee</th>
              <th>Requested On</th>
              <th>Type</th>
              <th>From</th>
              <th>To</th>
              <th>Reason</th>
              <th>Duration</th>
              <th>Hours</th>
              <th>Time Out</th>
              <th>Time In</th>
              <th>Status</th>
              <th>Remark</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {filteredLeaves.map((leave) => {
              const from = leave.fromDate
                ? new Date(leave.fromDate).toLocaleDateString()
                : "-";
              const to = leave.toDate
                ? new Date(leave.toDate).toLocaleDateString()
                : "-";

              const timeOut =
                leave.duration === "Half Day" &&
                (leave.halfDayOutTime || leave.halfDayTime)
                  ? formatTime12(leave.halfDayOutTime || leave.halfDayTime)
                  : "-";

              const timeIn =
                leave.duration === "Half Day" && leave.halfDayInTime
                  ? formatTime12(leave.halfDayInTime)
                  : "-";

              const hours = calcHours(leave);
              const requestedOn = formatRequestedOn(leave);

              return (
                <tr key={leave._id}>
                  <td>{leave.userName}</td>
                  <td className="lr-nowrap">{requestedOn}</td>
                  <td>{leave.type}</td>
                  <td>{from}</td>
                  <td>{to}</td>

                  <td className="lr-reason">{leave.reason || "-"}</td>

                  <td>{leave.duration || "Full Day"}</td>
                  <td>{hours}</td>
                  <td>{timeOut}</td>
                  <td>{timeIn}</td>

                  <td>
                    <span className={statusClass(leave.status)}>
                      {leave.status}
                    </span>
                  </td>

                  <td className="lr-remark-cell">
                    {leave.status === "Pending" ? (
                      <textarea
                        placeholder="Add remark"
                        value={remarks[leave._id] || ""}
                        onChange={(e) =>
                          setRemarks((p) => ({
                            ...p,
                            [leave._id]: e.target.value,
                          }))
                        }
                        rows={2}
                        className="lr-remark-input"
                      />
                    ) : leave.remark ? (
                      <div className="lr-remark-text">📝 {leave.remark}</div>
                    ) : (
                      "-"
                    )}
                  </td>

                  <td>
                    {leave.status === "Pending" ? (
                      <div className="lr-actions">
                        <button
                          onClick={() =>
                            handleLeaveStatus(leave._id, "Approved")
                          }
                          className="lr-btn lr-btn--approve"
                          type="button"
                        >
                          ✅ Approve
                        </button>
                        <button
                          onClick={() =>
                            handleLeaveStatus(leave._id, "Rejected")
                          }
                          className="lr-btn lr-btn--reject"
                          type="button"
                        >
                          ❌ Reject
                        </button>
                      </div>
                    ) : (
                      "-"
                    )}
                  </td>
                </tr>
              );
            })}

            {filteredLeaves.length === 0 && (
              <tr>
                <td className="lr-empty" colSpan={13}>
                  {emptyText}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LeaveRequest;
